import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { THEME } from "../ui/theme";
import { save } from "../systems/Save";
import {
  ACHIEVEMENT_DEFS, AchievementCategory, AchievementDef, TIER_DEFAULTS
} from "../systems/achievementDefs";
import { addCastleBackdrop, addContentPanel, addMenuBackdrop, addMenuHeader, addQuitButton, beginSceneLifecycle, MENU_CONTENT } from "../ui/menuUi";

// ── Layout constants ────────────────────────────────────────────────────────
const RAIL_X      = 40;
const RAIL_Y      = 128;
const RAIL_W      = 250;
const CARD_X      = RAIL_X + RAIL_W + 16;
const CARD_Y_TOP  = 128;
const CARD_H_FULL = GAME_HEIGHT - CARD_Y_TOP - 42;
const CARD_W      = GAME_WIDTH - CARD_X - 38;
const CARD_H      = 96;
const CARD_GAP    = 6;
const CARD_STRIDE = CARD_H + CARD_GAP;

// One achievement card per row, spanning the full list width.
const COL_W = CARD_W;

const ROW_BG_INACTIVE = 0x4a3420; // category row's brown fill
const ROW_BG_HOVER    = 0x322314; // category row's darker brown on hover

// Every measurement below was taken on the master art in
// public/ui/achievements/. The build ships half-resolution WebPs of those
// PNGs (scripts/compress-assets.mjs), so nothing may use these numbers as
// texture pixels directly — run them through srcRect()/borderSlice(), which
// rescale them to whichever texture actually loaded.
type SrcMeasure = { l: number; t: number; r: number; b: number; w: number; h: number };
type SrcRect = { l: number; t: number; w: number; h: number };

// progress-indicator.png: visible pixel bounds (PI_SRC) and the fill trough
// inside it (PI_TROUGH, as fractions of PI_SRC), since the 1024×1024 master
// has transparent margins.
const PI_SRC: SrcMeasure = { l: 61, t: 263, r: 955, b: 680, w: 1024, h: 1024 };
const PI_TROUGH = { l: 0.377, t: 0.367, r: 0.949, b: 0.746 };
const PI_FILL_COLOR = 0xf2a63a; // trough fill color
const PI_H = 72; // on-screen height of the indicator

// shield.png / coin.png visible pixel bounds — same idea as PI_SRC, both
// 1024×1024 masters with transparent margins.
const SHIELD_SRC: SrcMeasure = { l: 105, t: 45, r: 920, b: 986, w: 1024, h: 1024 };
const COIN_SRC: SrcMeasure   = { l: 135, t: 121, r: 888, b: 886, w: 1024, h: 1024 };

// achievement-border.png: 2048×512 gold frame shared by sidebar rows and
// cards. corner = size of the unstretched corner-ornament block for nine-slicing.
const ACH_BORDER = { w: 2048, h: 512, corner: 110 };

type CategoryFilter = AchievementCategory | "all";

const ALL_CATEGORIES: { id: CategoryFilter; label: string }[] = [
  { id: "all",       label: "All"      },
  { id: "campaign",  label: "Campaign" },
  { id: "slayer",    label: "Slayer"   },
  { id: "challenge", label: "Challenge"},
  { id: "mastery",   label: "Mastery"  },
  { id: "meta",      label: "Meta"     },
];

const CATEGORY_ICONS: Partial<Record<CategoryFilter, string>> = {
  all:       "icon-compass",
  campaign:  "icon-swords",
  slayer:    "icon-skull",
  challenge: "icon-trophy",
  mastery:   "icon-book",
  meta:      "icon-globe",
};

export class AchievementsScene extends Phaser.Scene {
  private currentCategory: CategoryFilter = "all";
  private scrollY = 0;
  private maxScroll = 0;
  private cardContainer!: Phaser.GameObjects.Container;
  private scrollBar?: Phaser.GameObjects.Rectangle;
  private scrollBarTrack?: Phaser.GameObjects.Rectangle;

  /** Filter rail buttons keyed by category for live highlighting. */
  private filterButtons: Map<CategoryFilter, Phaser.GameObjects.Container> = new Map();

  /** Bound wheel handler so we can detach it cleanly on shutdown. */
  private wheelHandler?: (
    ptr: Phaser.Input.Pointer,
    over: Phaser.GameObjects.GameObject[],
    dx: number, dy: number
  ) => void;

  constructor() { super("AchievementsScene"); }

  create(): void {
    const lifecycle = beginSceneLifecycle(this);
    this.currentCategory = (this.registry.get("achieveCat") as CategoryFilter) ?? "all";
    this.scrollY = 0;
    this.filterButtons.clear();

    addMenuBackdrop(this, { darkness: 0.76 });
    addContentPanel(this, { x: 24, y: 96, width: GAME_WIDTH - 48, height: GAME_HEIGHT - 116 }, "iron", 0.97);
    // Castle art fills the whole panel; drawn here so it sits above the iron
    // plate but behind the header text, filter rail, and cards.
    this.buildListBackdrop();
    addMenuHeader(this, {
      title: "ACHIEVEMENTS",
      subtitle: "Every victory earns its place in the chronicle.",
      onBack: () => goTo(this, "MenuScene"),
    });
    addQuitButton(this);
    lifecycle.key("keydown-ESC", () => goTo(this, "MenuScene"));
    lifecycle.key("keydown-UP", () => this.applyScroll(-CARD_STRIDE));
    lifecycle.key("keydown-DOWN", () => this.applyScroll(CARD_STRIDE));
    lifecycle.key("keydown-PAGEUP", () => this.applyScroll(-CARD_H_FULL * 0.8));
    lifecycle.key("keydown-PAGEDOWN", () => this.applyScroll(CARD_H_FULL * 0.8));

    const data = save.get();
    const totalDefs = Object.keys(ACHIEVEMENT_DEFS).length;
    const totalUnlocked = Object.keys(data.achievements?.unlocked ?? {}).length;
    this.buildStatsBanner(totalUnlocked, totalDefs);

    this.buildFilterRail();
    this.buildMask();
    this.rebuildCards();
    this.wireScroll();

    // Detach the wheel handler when this scene shuts down so we don't
    // accumulate listeners across navigations.
    lifecycle.add(() => {
      if (this.wheelHandler) {
        this.input.off("wheel", this.wheelHandler);
        this.wheelHandler = undefined;
      }
    });
  }

  // ── Category filter list ───────────────────────────────────────────────────
  private buildFilterRail(): void {
    const data = save.get();
    const ROW_H = 70;
    ALL_CATEGORIES.forEach((cat, i) => {
      const y = RAIL_Y + i * ROW_H;
      const defs = Object.values(ACHIEVEMENT_DEFS).filter(d =>
        cat.id === "all" || d.category === cat.id
      );
      const total = defs.length;
      const unlocked = defs.filter(d => !!data.achievements?.unlocked[d.id]).length;
      const pct = total > 0 ? Math.round((unlocked / total) * 100) : 0;

      const row = this.add.container(RAIL_X, y);

      const rowBg = this.add.rectangle(0, 0, RAIL_W, ROW_H, THEME.hex.ironDeep, 0.85)
        .setOrigin(0, 0)
        .setStrokeStyle(1, THEME.hex.ironEdge, 0.5)
        .setInteractive({ useHandCursor: true })
        .on("pointerdown", () => this.setCategory(cat.id))
        .on("pointerover", () => {
          if (cat.id !== this.currentCategory) {
            rowBg.setFillStyle(ROW_BG_HOVER, 0.85);
            rowBg.setStrokeStyle(1, THEME.hex.gold, 0.4);
            (row.getData("hoverBorder") as Phaser.GameObjects.Image).setVisible(true);
          }
        })
        .on("pointerout", () => {
          if (cat.id !== this.currentCategory) {
            rowBg.setFillStyle(ROW_BG_INACTIVE, 0.85);
            rowBg.setStrokeStyle(1, THEME.hex.ironEdge, 0.5);
            (row.getData("hoverBorder") as Phaser.GameObjects.Image).setVisible(false);
          }
        });
      row.add(rowBg);

      const divider = this.add.rectangle(0, ROW_H, RAIL_W, 1, THEME.hex.ironEdge, 0.35)
        .setOrigin(0, 0.5);
      row.add(divider);

      const iconKey = CATEGORY_ICONS[cat.id];
      const iconWidth = cat.id === "all" ? 35.9 : 32.7;
      const labelX = iconKey ? 55 : 10;
      if (iconKey) {
        const icon = this.add.image(10, ROW_H / 2, iconKey).setOrigin(0, 0.5);
        icon.displayWidth = iconWidth;
        icon.scaleY = icon.scaleX;
        row.add(icon);
      }

      const label = this.add.text(labelX, ROW_H / 2, cat.label.toUpperCase(), {
        fontFamily: THEME.fonts.display, fontSize: "15px",
        color: cat.id === this.currentCategory ? THEME.colors.gold : THEME.colors.parchmentDim,
      }).setOrigin(0, 0.5);
      row.add(label);

      const countLabel = this.add.text(RAIL_W - 21, ROW_H / 2 - 10, `${unlocked} / ${total}`, {
        fontFamily: THEME.fonts.body, fontSize: "17px", color: THEME.colors.parchment,
      }).setOrigin(1, 0.5);
      row.add(countLabel);

      const pctLabel = this.add.text(RAIL_W - 21, ROW_H / 2 + 10, `${pct}%`, {
        fontFamily: THEME.fonts.body, fontSize: "17px", color: THEME.colors.gold,
      }).setOrigin(1, 0.5);
      row.add(pctLabel);

      // Active-filter border; overscanned slightly since the art has a
      // transparent margin, so its edge lines up with the row below.
      const BORDER_OVERSCAN_X = RAIL_W * 0.02;
      const BORDER_OVERSCAN_Y = 4.7;
      const selectedBorder = this.add.image(RAIL_W / 2, ROW_H / 2, "frame-medieval-border")
        .setOrigin(0.5, 0.5)
        .setDisplaySize(RAIL_W + BORDER_OVERSCAN_X, ROW_H + BORDER_OVERSCAN_Y)
        .setVisible(cat.id === this.currentCategory);
      row.add(selectedBorder);

      // Same ornate frame as the selected state, but dimmed — a preview of
      // the click state rather than a match for its intensity.
      const hoverBorder = this.add.image(RAIL_W / 2, ROW_H / 2, "frame-medieval-border")
        .setOrigin(0.5, 0.5)
        .setDisplaySize(RAIL_W + BORDER_OVERSCAN_X, ROW_H + BORDER_OVERSCAN_Y)
        .setAlpha(0.45)
        .setVisible(false);
      row.add(hoverBorder);

      row.setData("bg", rowBg);
      row.setData("label", label);
      row.setData("selectedBorder", selectedBorder);
      row.setData("hoverBorder", hoverBorder);
      this.filterButtons.set(cat.id, row);
    });
    this.updateFilterHighlights();
  }

  private setCategory(cat: CategoryFilter): void {
    if (this.currentCategory === cat) return;
    this.currentCategory = cat;
    this.registry.set("achieveCat", cat);
    this.scrollY = 0;
    this.updateFilterHighlights();
    this.rebuildCards();
  }

  private updateFilterHighlights(): void {
    for (const [id, row] of this.filterButtons) {
      const bg            = row.getData("bg")            as Phaser.GameObjects.Rectangle;
      const label         = row.getData("label")         as Phaser.GameObjects.Text;
      const selectedBorder = row.getData("selectedBorder") as Phaser.GameObjects.Image;
      const hoverBorder   = row.getData("hoverBorder")    as Phaser.GameObjects.Image;
      const active = id === this.currentCategory;
      bg.setFillStyle(active ? THEME.hex.ironDeep : ROW_BG_INACTIVE, active ? 1 : 0.85);
      bg.setStrokeStyle(1, active ? THEME.hex.gold : THEME.hex.ironEdge, active ? 0 : 0.5);
      label.setColor(active ? THEME.colors.gold : THEME.colors.parchmentDim);
      selectedBorder.setVisible(active);
      if (active) hoverBorder.setVisible(false);
    }
  }

  // ── Painted-art measurements ──────────────────────────────────────────────
  /**
   * Rescales a master-art measurement to the texture that actually loaded, so
   * the layout survives the PNG → half-resolution WebP swap the portal build
   * does. Returns the visible region's offset and size in texture pixels.
   */
  private srcRect(key: string, m: SrcMeasure): SrcRect {
    const src = this.textures.get(key).source[0];
    const kx = src.width / m.w;
    const ky = src.height / m.h;
    return { l: m.l * kx, t: m.t * ky, w: (m.r - m.l) * kx, h: (m.b - m.t) * ky };
  }

  /** Nine-slice geometry for the gold card frame at the loaded resolution. */
  private borderSlice(): { texH: number; cornerX: number; cornerY: number } {
    const src = this.textures.get("frame-achievement-border").source[0];
    return {
      texH: src.height,
      cornerX: ACH_BORDER.corner * (src.width / ACH_BORDER.w),
      cornerY: ACH_BORDER.corner * (src.height / ACH_BORDER.h),
    };
  }

  // ── Backdrop ──────────────────────────────────────────────────────────────
  // Castle art spanning the whole panel, behind the rail and cards.
  private buildListBackdrop(): void {
    addCastleBackdrop(this, MENU_CONTENT, { veil: "parchment" });
  }

  // ── Stats text ────────────────────────────────────────────────────────────
  // "X / Y Unlocked" above the card list, right-aligned to it.
  private buildStatsBanner(unlocked: number, total: number): void {
    this.add.text(GAME_WIDTH - 48, 112, `${unlocked}/${total} Unlocked`, {
      fontFamily: THEME.fonts.body, fontSize: "16px", fontStyle: "500",
      color: THEME.colors.ironDeep,
    }).setOrigin(1, 0.5);
  }

  // ── Scrollable mask ───────────────────────────────────────────────────────
  private buildMask(): void {
    // Bleeds a bit horizontally so the card border's overscanned edges stay
    // visible; stays tight vertically to clip scrolled-off cards.
    const MASK_BLEED_X = 14;
    const maskShape = this.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(CARD_X - MASK_BLEED_X, CARD_Y_TOP, CARD_W + MASK_BLEED_X * 2, CARD_H_FULL);
    const mask = new Phaser.Display.Masks.GeometryMask(this, maskShape);

    this.cardContainer = this.add.container(0, 0);
    this.cardContainer.setMask(mask);
  }

  // ── Achievement cards ─────────────────────────────────────────────────────
  private rebuildCards(): void {
    this.cardContainer.removeAll(true);

    const data = save.get();
    const defs = Object.values(ACHIEVEMENT_DEFS).filter(d =>
      this.currentCategory === "all" || d.category === this.currentCategory
    );

    // Sort: unlocked first (most recent first), then locked (hidden last)
    defs.sort((a, b) => {
      const ua = data.achievements?.unlocked[a.id] ?? 0;
      const ub = data.achievements?.unlocked[b.id] ?? 0;
      if (ua && ub) return ub - ua;
      if (ua) return -1;
      if (ub) return 1;
      if (a.hidden && !b.hidden) return 1;
      if (!a.hidden && b.hidden) return -1;
      return 0;
    });

    const rows = defs.length;
    const contentH = rows * CARD_STRIDE;
    this.maxScroll = Math.max(0, contentH - CARD_H_FULL);

    defs.forEach((def, i) => {
      const cardX = CARD_X;
      const cy = CARD_Y_TOP + i * CARD_STRIDE;
      const unlocked = !!(data.achievements?.unlocked[def.id]);
      const progress = data.achievements?.progress[def.id] ?? 0;
      const isHidden = !!(def.hidden && !unlocked);
      this.buildCard(def, cardX, cy, unlocked, progress, isHidden);
    });

    this.cardContainer.y = -this.scrollY;
    this.rebuildScrollBar();
  }

  private buildCard(
    def: AchievementDef,
    cardX: number,
    cy: number,
    unlocked: boolean,
    progress: number,
    isHidden: boolean,
  ): void {
    // Locked cards stay bare (castle showing through, dark-ink text);
    // unlocked cards get a solid ironDeep plate behind gold/parchment text.
    const ink = THEME.colors.ironDeep;
    // Plate matches the frame's (overscanned) footprint, inset slightly so
    // the gold border edge always draws over it.
    const borderTargetW = COL_W + COL_W * 0.02;
    const borderTargetH = CARD_H + 4.7;
    if (unlocked) {
      const bg = this.add.rectangle(
        cardX + COL_W / 2, cy + CARD_H / 2, borderTargetW - 5, borderTargetH - 5, THEME.hex.ironDeep, 1,
      );
      this.cardContainer.add(bg);
    }
    // Same frame as the sidebar rows, nine-sliced so the corner ornaments
    // keep their shape while the middle rails stretch to the card's width.
    // Height is left at the texture's own so only the horizontal middle
    // stretches — sliced any taller, the thin mid band smears into a seam.
    const slice = this.borderSlice();
    const borderScale = borderTargetH / slice.texH;
    const border = this.add.nineslice(
      cardX + COL_W / 2, cy + CARD_H / 2,
      "frame-achievement-border", undefined,
      borderTargetW / borderScale, slice.texH,
      slice.cornerX, slice.cornerX,
      slice.cornerY, slice.cornerY,
    ).setScale(borderScale);
    this.cardContainer.add(border);

    const textX = cardX + 20;

    // Reward figures computed up front — the text-wrap and progress-indicator
    // placement below depend on them.
    const defaults = TIER_DEFAULTS[def.tier];
    const xp = def.reward.xp ?? defaults.xp ?? 0;
    const sp = def.reward.skillPoints ?? defaults.skillPoints ?? 0;

    // Shield/coin medallion half-sizes. Height drives the fit; width follows
    // each art's own aspect ratio so it isn't stretched.
    const shieldSrc = this.srcRect("icon-shield", SHIELD_SRC);
    const coinSrc   = this.srcRect("icon-coin", COIN_SRC);
    const shieldH = 38; // half-height
    const shieldW = (shieldH * shieldSrc.w) / shieldSrc.h; // half-width
    const coinH = shieldH - 5; // half-height
    const coinW = (coinH * coinSrc.w) / coinSrc.h; // half-width

    const REWARD_RIGHT_MARGIN = 30;
    const ICON_GAP = 10;
    // The XP reward sits in a column on the right; text wraps before it.
    const rewardCx = sp > 0
      ? cardX + COL_W - REWARD_RIGHT_MARGIN - coinW * 2 - ICON_GAP - shieldW
      : cardX + COL_W - REWARD_RIGHT_MARGIN - shieldW;
    // Countable achievements also carry the stone progress indicator to the
    // left of the XP shield; their text must wrap before the indicator.
    const target = def.target ?? 0;
    const hasTarget = target > 0;
    const piSrc = this.srcRect("progress-indicator", PI_SRC);
    const piScale = PI_H / piSrc.h;
    const piW = piSrc.w * piScale;
    const piRight = rewardCx - shieldW - 8; // shield's left edge minus a gap
    const piLeft = piRight - piW;
    // Unlocked cards with nothing to count put their earn date in the same
    // column, so they have to reserve it from the text just like the
    // indicator does.
    const hasIndicatorColumn = hasTarget || unlocked;
    const textRight = hasIndicatorColumn ? piLeft - 12 : rewardCx - 40;

    if (isHidden) {
      const hiddenName = this.add.text(textX, cy + 20, "???", {
        fontFamily: THEME.fonts.display, fontSize: "18px", fontStyle: "600", color: ink,
      }).setOrigin(0, 0);
      const hiddenDesc = this.add.text(textX, cy + 48, "Unlock to reveal.", {
        fontFamily: THEME.fonts.body, fontSize: "15px", color: ink,
      }).setOrigin(0, 0);
      this.cardContainer.add([hiddenName, hiddenDesc]);
      return;
    }

    // Title + description fill the framed interior, title on top.
    const nameLabel = this.add.text(textX, cy + 20, def.name.toUpperCase(), {
      fontFamily: THEME.fonts.display, fontSize: "18px", fontStyle: "600",
      color: unlocked ? THEME.colors.gold : ink,
      wordWrap: { width: textRight - textX },
    }).setOrigin(0, 0);
    this.cardContainer.add(nameLabel);

    const descLabel = this.add.text(textX, cy + 48, def.desc, {
      fontFamily: THEME.fonts.body, fontSize: "15px",
      color: unlocked ? THEME.colors.parchmentDim : ink,
      wordWrap: { width: textRight - textX },
    }).setOrigin(0, 0);
    this.cardContainer.add(descLabel);

    // XP value + caption centered in the shield medallion at the card's right.
    const rewardCy = cy + CARD_H / 2;
    const shieldScale = (shieldH * 2) / shieldSrc.h;
    const shieldTop = rewardCy - shieldH;
    const shieldLeft = rewardCx - shieldW;
    const shieldIcon = this.add.image(
      shieldLeft - shieldSrc.l * shieldScale, shieldTop - shieldSrc.t * shieldScale, "icon-shield")
      .setOrigin(0, 0)
      .setScale(shieldScale)
      // Locked achievements read as faded/incomplete, matching the bare
      // styling of the rest of a locked card; unlocked ones show full art.
      .setAlpha(unlocked ? 1 : 0.45);
    this.cardContainer.add(shieldIcon);

    const xpText = xp >= 1000 ? `+${(xp / 1000).toFixed(xp % 1000 === 0 ? 0 : 1)}k` : `+${xp}`;
    // Nudged above center to offset the shield art's visual weight.
    const xpLabel = this.add.text(rewardCx, rewardCy - 10, xpText, {
      fontFamily: THEME.fonts.display, fontSize: "18px", fontStyle: "600",
      color: ink,
    }).setOrigin(0.5);
    this.cardContainer.add(xpLabel);

    const xpCaption = this.add.text(rewardCx, rewardCy + 8, "XP", {
      fontFamily: THEME.fonts.body, fontSize: "17px", fontStyle: "600",
      color: ink,
    }).setOrigin(0.5);
    this.cardContainer.add(xpCaption);

    // SP reward: coin medallion right of the shield, mirrors its rendering.
    if (sp > 0) {
      const coinScale = (coinH * 2) / coinSrc.h;
      const coinCx = rewardCx + shieldW + ICON_GAP + coinW;
      const coinTop = rewardCy - coinH;
      const coinLeft = coinCx - coinW;
      const coinIcon = this.add.image(
        coinLeft - coinSrc.l * coinScale, coinTop - coinSrc.t * coinScale, "icon-coin")
        .setOrigin(0, 0)
        .setScale(coinScale)
        .setAlpha(unlocked ? 1 : 0.45);
      this.cardContainer.add(coinIcon);

      const spLabel = this.add.text(coinCx, rewardCy - 10, `+${sp}`, {
        fontFamily: THEME.fonts.display, fontSize: "18px", fontStyle: "600",
        color: ink,
      }).setOrigin(0.5);
      this.cardContainer.add(spLabel);

      const spCaption = this.add.text(coinCx, rewardCy + 8, "SP", {
        fontFamily: THEME.fonts.body, fontSize: "17px", fontStyle: "600",
        color: ink,
      }).setOrigin(0.5);
      this.cardContainer.add(spCaption);
    }

    // Earn date in the indicator column, centered — countable cards render
    // their own copy under the trough instead (below).
    if (unlocked && !hasTarget) {
      const ts = save.get().achievements.unlocked[def.id];
      const date = ts ? new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "";
      const check = this.add.text((piLeft + piRight) / 2, rewardCy, `✓ ${date}`, {
        fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim,
      }).setOrigin(0.5);
      this.cardContainer.add(check);
    }

    // Countable achievements (e.g. "kill 200 goblins") get a progress
    // indicator left of the XP shield, trough filled proportionally.
    if (hasTarget) {
      const capped = unlocked ? target : Math.min(progress, target);
      const pct = Math.min(1, capped / target);

      // Placed by its measured content bounds (PI_SRC) onto (piLeft..piRight).
      const piTop = rewardCy - PI_H / 2;
      const img = this.add.image(
        piLeft - piSrc.l * piScale, piTop - piSrc.t * piScale, "progress-indicator")
        .setOrigin(0, 0)
        .setScale(piScale);
      this.cardContainer.add(img);

      // Amber fill, inset slightly so the art's beveled rim stays visible.
      if (pct > 0) {
        const tx = piLeft + PI_TROUGH.l * piW + 2;
        const ty = piTop + PI_TROUGH.t * PI_H + 1;
        const tw = (PI_TROUGH.r - PI_TROUGH.l) * piW - 4;
        const th = (PI_TROUGH.b - PI_TROUGH.t) * PI_H - 4;
        const fillW = Math.max(3, tw * pct);
        const fill = this.add.graphics();
        // Soft dark halo around the fill: stacked expanding layers fake a blur,
        // since Phaser Graphics has no native shadow.
        fill.fillStyle(0x000000, 0.18);
        for (let i = 3; i >= 1; i--) {
          fill.fillRoundedRect(tx - i, ty - i, fillW + i * 2, th + i * 2,
            Math.min(6 + i, (fillW + i * 2) / 2, (th + i * 2) / 2));
        }
        fill.fillStyle(PI_FILL_COLOR, 1);
        fill.fillRoundedRect(tx, ty, fillW, th, Math.min(6, fillW / 2, th / 2));
        this.cardContainer.add(fill);
      }

      // Exact count above the trough, within the indicator's own frame band.
      const countLabel = this.add.text(
        piLeft + (PI_TROUGH.l + PI_TROUGH.r) / 2 * piW,
        piTop + PI_TROUGH.t * PI_H - 7,
        `${capped.toLocaleString()} / ${target.toLocaleString()}`, {
          fontFamily: THEME.fonts.body, fontSize: "18px",
          color: unlocked ? THEME.colors.parchmentDim : ink,
        }).setOrigin(0.5, 1);
      this.cardContainer.add(countLabel);

      // Earn date under the trough, mirroring the count above it.
      if (unlocked) {
        const ts = save.get().achievements.unlocked[def.id];
        const date = ts ? new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "";
        const check = this.add.text(
          piLeft + (PI_TROUGH.l + PI_TROUGH.r) / 2 * piW,
          piTop + PI_TROUGH.b * PI_H + 7,
          `✓ ${date}`, {
            fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim,
          }).setOrigin(0.5, 0);
        this.cardContainer.add(check);
      }
    }
  }

  // ── Scroll bar ────────────────────────────────────────────────────────────
  private rebuildScrollBar(): void {
    this.scrollBar?.destroy();      this.scrollBar = undefined;
    this.scrollBarTrack?.destroy(); this.scrollBarTrack = undefined;
    if (this.maxScroll <= 0) return;

    const barX = GAME_WIDTH - 14;
    const trackH = CARD_H_FULL;
    this.scrollBarTrack = this.add.rectangle(barX, CARD_Y_TOP, 6, trackH, THEME.hex.iron, 0.6)
      .setOrigin(0.5, 0).setStrokeStyle(1, THEME.hex.ironEdge, 0.4);

    const thumbH = Math.max(40, trackH * (CARD_H_FULL / (CARD_H_FULL + this.maxScroll)));
    this.scrollBar = this.add.rectangle(barX, CARD_Y_TOP, 6, thumbH, THEME.hex.goldDim, 0.85)
      .setOrigin(0.5, 0);
    this.updateScrollBar();
  }

  private updateScrollBar(): void {
    if (!this.scrollBar || this.maxScroll <= 0) return;
    const trackH = CARD_H_FULL;
    const thumbH = this.scrollBar.height;
    const pct = this.scrollY / this.maxScroll;
    this.scrollBar.y = CARD_Y_TOP + pct * (trackH - thumbH);
  }

  // ── Scroll wiring ─────────────────────────────────────────────────────────
  private wireScroll(): void {
    this.wheelHandler = (_p, _o, _dx, dy) => this.applyScroll(dy * 1.2);
    this.input.on("wheel", this.wheelHandler);
  }

  private applyScroll(delta: number): void {
    this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, 0, this.maxScroll);
    this.cardContainer.y = -this.scrollY;
    this.updateScrollBar();
  }
}
